import {
  Box,
  Button,
  Autocomplete,
  Chip,
  TextField,
  Menu,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { OrionIconButton } from "@orion/OrionComponents/OrionIconButton";
import { useState, type ReactNode } from "react";

import { bannerTooltipSlotProps } from "@/components/bannerTooltip";
import {
  isValidUsStoredNumber,
  loadPersistedPhonesFromKey,
  parseUsPhoneToE164,
  persistPhonesToKey,
  phoneOptionFromE164,
} from "@/features/TextComposerV2/preview/recipientPhones";
import { LockIcon, SendIcon } from "@/icons";

export type TestSmsAdapter<Scope> = {
  storageKey(scope: Scope): string;
  useHandler(scope: Scope): {
    onSend: (phones: string[]) => void;
    isSending: boolean;
  };
  buttonText?: string;
  beforeSend?: () => Promise<boolean | void>;
};

type PhoneOption = { id: string; name: string };

type TestSmsBarProps<Scope> = {
  scope: Scope;
  adapter: TestSmsAdapter<Scope>;
  buttonType?: "icon" | "button";
  buttonVariant?: "text" | "contained" | "outlined" | undefined;
  iconColor?: string;
  disabled?: boolean;
  /**
   * Capability gate: when true the trigger is blocked and shows a lock icon
   * (e.g. the site doesn't have SMS enabled). Distinct from `disabled`, which
   * covers content/validity reasons.
   */
  locked?: boolean;
  /** Accessible label / fallback tooltip text used while `locked`. */
  lockedReason?: string;
  /**
   * Rich tooltip content shown while `locked` (e.g. the SMS-not-enabled banner).
   * Rendered as a bare surface so the node itself is the popup.
   */
  lockedTooltip?: ReactNode;
};

export function TestSmsBar<Scope>({
  scope,
  adapter,
  buttonType = "button",
  buttonVariant = "text",
  iconColor = "secondary.main",
  disabled = false,
  locked = false,
  lockedReason,
  lockedTooltip,
}: TestSmsBarProps<Scope>) {
  const { onSend, isSending } = adapter.useHandler(scope);
  const isBlocked = disabled || locked;
  const triggerLabel = locked
    ? (lockedReason ?? adapter.buttonText ?? "Send test text")
    : (adapter.buttonText ?? "Send test text");
  const triggerTooltip =
    locked && lockedTooltip != null ? lockedTooltip : triggerLabel;
  const triggerTooltipSlotProps =
    locked && lockedTooltip != null ? bannerTooltipSlotProps : undefined;
  const TriggerIcon = locked ? LockIcon : SendIcon;

  const getStorageKey = () => adapter.storageKey(scope);

  const getInitialPhones = (): PhoneOption[] =>
    loadPersistedPhonesFromKey(getStorageKey());

  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<PhoneOption[]>(getInitialPhones);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSendTestSms = async () => {
    const phones = value.map((v) => v.id);

    if (!adapter.beforeSend) {
      onSend(phones);
      handleClose();
    }

    try {
      const ok = await adapter.beforeSend?.();

      if (!ok) return;

      onSend(phones);
      handleClose();
    } catch (error) {
      console.error("Failed to send test SMS: ", error);
      return;
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {buttonType === "icon" ? (
        <Tooltip
          title={triggerTooltip}
          placement="top"
          slotProps={triggerTooltipSlotProps}
        >
          {buttonVariant === "outlined" ? (
            <span>
              <OrionIconButton
                variant="outlined"
                onClick={handleClick}
                size="small"
                disabled={isBlocked}
                aria-label={triggerLabel}
              >
                <TriggerIcon fontSize="small" />
              </OrionIconButton>
            </span>
          ) : (
            <span>
              <IconButton
                color="primary"
                onClick={handleClick}
                aria-label={triggerLabel}
                disabled={isBlocked}
                sx={{
                  color: isBlocked ? "text.disabled" : iconColor,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <TriggerIcon fontSize="small" />
              </IconButton>
            </span>
          )}
        </Tooltip>
      ) : (
        <Button
          variant={buttonVariant}
          onClick={handleClick}
          disabled={isBlocked}
          startIcon={<TriggerIcon sx={{ fontSize: "20px" }} />}
          sx={{
            color: isBlocked ? "text.disabled" : iconColor,
            fontSize: "15px",
            fontWeight: 500,
            letterSpacing: "0.46px",
            textTransform: "uppercase",
            padding: (theme) => theme.spacing(1, 2),
            gap: (theme) => theme.spacing(2),
            "&:hover": {
              backgroundColor: "transparent",
            },
            "& .MuiButton-startIcon": {
              marginLeft: 0,
              marginRight: (theme) => theme.spacing(3),
            },
          }}
        >
          {adapter.buttonText ?? "Test It"}
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              padding: "16px",
              minWidth: "400px",
              borderRadius: "8px",
              backgroundColor: "background.paper-elevation-2",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            value={value}
            onChange={(_, newValue) => {
              // Normalize to E.164, dropping anything that isn't a valid US
              // number (mirrors RecipientView).
              const normalized = newValue
                .map((v): PhoneOption | null => {
                  if (typeof v === "string") {
                    const id = parseUsPhoneToE164(v);
                    return id ? phoneOptionFromE164(id) : null;
                  }
                  return typeof v.id === "string" && isValidUsStoredNumber(v.id)
                    ? phoneOptionFromE164(v.id)
                    : null;
                })
                .filter((v): v is PhoneOption => v !== null);

              setValue(normalized);
              persistPhonesToKey(getStorageKey(), normalized);
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            filterOptions={(_, params) => {
              const candidate = parseUsPhoneToE164(params.inputValue);
              if (candidate) {
                return [phoneOptionFromE164(candidate)];
              }
              return [];
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Test Phone Numbers"
                placeholder="Add phone numbers"
                size="small"
                helperText="Enter phone numbers in any format (e.g., 5554443333, +1-555-444-3333)"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={option.name}
                  key={option.id}
                  size="small"
                />
              ))
            }
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string")
                return option === value;
              return option.id === value.id;
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSendTestSms()}
            disabled={value.length === 0 || isSending}
            fullWidth
            startIcon={isSending ? <CircularProgress size={16} /> : null}
          >
            {isSending ? "Sending..." : "Send Test Text"}
          </Button>
        </Box>
      </Menu>
    </Box>
  );
}
